// TTS API client — Plumescript Pro + Personal narrator tiers.
//
// Two backends behind the same axios client (``aiClient``):
//   * POST /tts/synthesize      → Polly Generative (Pro tier)
//   * POST /tts/synthesize-cloned → F5-TTS on SageMaker (Personal tier)
//
// The Pro tier returns either a JSON ``audio_url`` (when the
// backend has a CDN configured) or raw MP3 bytes inline. The
// Personal tier always returns raw WAV inline.
//
// Both wrappers normalise to a Blob URL the caller drops into an
// ``<audio src=…>`` element. The Blob URL is owned by the caller —
// remember to revokeObjectURL when the writer leaves the chapter
// to avoid memory creep.

import { aiClient } from './apiClient';
import { logger } from './logger';
import { consumeQuota } from './quotaApi';
import { QuotaExceededError } from './quotaErrors';

/**
 * Catalogue of available Pro narrator voices (Polly Generative +
 * Long-form). Public route — no auth, no quota.
 *
 * Returns: { voices: [{ voice_id, name, language_code, gender, engine }] }
 */
export async function listProVoices() {
  try {
    const { data } = await aiClient.get('/tts/voices');
    return data?.voices || [];
  } catch (err) {
    logger.warn('tts_voices_fetch_failed', { message: err?.message });
    return [];
  }
}

// Polly synchronous synth caps at ~real-time speed (~32s per
// 2800 chars on the Generative engine). Anything beyond this
// blows the 120s axios budget. The backend rejects sync calls
// over 5000 chars; we mirror that decision here so the frontend
// picks the right endpoint up-front.
const POLLY_SYNC_HARD_CAP_CHARS = 5000;

/**
 * Synthesize ``text`` with a Pro voice. Auto-routes based on
 * length:
 *   * text <  5000 chars → sync ``/tts/synthesize`` (one POST,
 *     audio returned inline or via CDN URL)
 *   * text >= 5000 chars → async ``/tts/synthesize-async`` (Polly
 *     StartSpeechSynthesisTask, frontend polls /tts/task/{id})
 *
 * Every call consumes ``ai_tts`` quota — fair per-writer
 * accounting regardless of whether the backend cache made the
 * actual Polly call free. Cache is a *cost* lever, not a user
 * entitlement (see fairness commit).
 *
 * Returns ``{ url, cleanup, cached, shared }`` —
 *   ``url``     : pointer for ``<audio src=…>``
 *   ``cleanup`` : revokeObjectURL when caller is done (Blob path only)
 *   ``cached``  : true if the audio existed in cache (instant playback)
 *   ``shared``  : alias of cached — UX hint only
 *
 * Async path also accepts ``onProgress({ elapsed_seconds, attempt })``
 * so the UI can show "still rendering… (28s)" without polling
 * state leaking into every caller.
 *
 * Throws ``QuotaExceededError`` when the writer is out of
 * ``ai_tts`` for the day.
 */
export async function synthesizePro({
  text, voiceId, ssml = false, planTier = 'free', engine = null,
  // Polly Generative async runs at ~14 s per 1K chars end-to-end
  // (live-measured: 5K→75s, 10K→145s, 20K→281s). 480 s covers
  // any chapter up to ~35K chars (~5500 words) — comfortably
  // beyond a normal novel chapter.
  onProgress = null, maxPollSeconds = 480,
  // ``quotaFeature`` lets the caller route the cost to a
  // different bucket. Live playback (the default) hits ``ai_tts``;
  // a chapter download saves to ``ai_tts_download`` so the writer
  // doesn't burn their playback budget when they're just saving
  // an audiobook chapter for offline.
  quotaFeature = 'ai_tts',
}) {
  if (!text || !voiceId) {
    throw new Error('synthesizePro: text and voiceId are required');
  }
  const body = {
    text, voice_id: voiceId, ssml,
    plan_tier: planTier,
    ...(engine ? { engine } : {}),
  };
  // Quota first, always. Fair per-writer accounting.
  const verdict = await consumeQuota(quotaFeature);
  if (!verdict?.allowed) {
    throw new QuotaExceededError(verdict);
  }

  // ---- ASYNC PATH for chapter-length content ----
  if (text.length >= POLLY_SYNC_HARD_CAP_CHARS) {
    return await _synthesizeProAsync({ body, onProgress, maxPollSeconds });
  }

  // ---- SYNC PATH for snippets ----
  let response;
  try {
    response = await aiClient.post('/tts/synthesize', body, { responseType: 'blob' });
  } catch (err) {
    logger.warn('tts_synthesize_failed', { message: err?.message, voice_id: voiceId });
    throw err;
  }

  const ct = response.headers?.['content-type'] || '';
  if (ct.includes('application/json')) {
    const responseText = await response.data.text();
    const payload = JSON.parse(responseText);
    if (payload.audio_url) {
      return {
        url: payload.audio_url, cleanup: () => {},
        cached: !!payload.cached, shared: !!payload.cached,
      };
    }
    throw new Error('synthesizePro: backend returned JSON without audio_url');
  }
  const blobUrl = URL.createObjectURL(response.data);
  const cached = response.headers?.['x-tts-cached'] === '1';
  return {
    url: blobUrl, cleanup: () => URL.revokeObjectURL(blobUrl),
    cached, shared: cached,
  };
}

async function _synthesizeProAsync({ body, onProgress, maxPollSeconds }) {
  // Submit — returns task_id + poll_after_seconds + cached flag.
  let submit;
  try {
    const { data } = await aiClient.post('/tts/synthesize-async', body);
    submit = data;
  } catch (err) {
    logger.warn('tts_synthesize_async_submit_failed', { message: err?.message });
    throw err;
  }
  const { task_id: taskId, poll_after_seconds: firstWait, cached: serverCached } = submit;

  // Cache hit short-circuit: backend returned the URL with the submit.
  if (serverCached && submit.audio_url) {
    if (onProgress) {
      try { onProgress({ elapsed_seconds: 0, attempt: 0, cached: true }); } catch { /* swallow */ }
    }
    return { url: submit.audio_url, cleanup: () => {}, cached: true, shared: true };
  }

  // Poll backoff: 5 → 5 → 8 → 12 → 18 → 27 → 45 → 60 s, capped
  // at maxPollSeconds (default 240 s). Polly Generative ~32 s per
  // 2800 chars; a 20K-char chapter takes ~240 s server-side so
  // we converge with ~6 polls in the worst case.
  const backoffs = [firstWait != null ? firstWait : 5, 5, 8, 12, 18, 27, 45, 60];
  const start = Date.now();
  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const wait = backoffs[Math.min(attempt, backoffs.length - 1)];
    await new Promise((r) => setTimeout(r, wait * 1000));
    attempt += 1;
    const elapsed = Math.round((Date.now() - start) / 1000);
    if (onProgress) {
      try { onProgress({ elapsed_seconds: elapsed, attempt }); } catch { /* swallow */ }
    }
    if (elapsed > maxPollSeconds) {
      throw new Error(`synthesizePro: async timed out after ${elapsed}s`);
    }
    let status;
    try {
      const { data } = await aiClient.get(`/tts/task/${taskId}`);
      status = data;
    } catch (err) {
      logger.warn('tts_async_poll_failed', { taskId, message: err?.message });
      throw err;
    }
    if (status.status === 'ready' && status.audio_url) {
      return {
        url: status.audio_url, cleanup: () => {},
        cached: !!serverCached, shared: !!serverCached,
      };
    }
    if (status.status === 'failed') {
      throw new Error(`synthesizePro: Polly reported failure — ${status.error || 'unknown'}`);
    }
  }
}

/**
 * Upload a writer's reference voice clip (6-15 s clean speech)
 * so subsequent ``synthesizePersonal`` calls can clone it via
 * F5-TTS. The returned pointer (``s3_key`` + ``sample_id``) is
 * what the caller passes to ``synthesizePersonal``.
 *
 * ``audioBlob`` is whatever the browser captured — typically a
 * MediaRecorder Blob (audio/webm) or a user-uploaded file.
 */
export async function uploadVoiceSample({ audioBlob, referenceText, language = 'en' }) {
  if (!audioBlob || !referenceText) {
    throw new Error('uploadVoiceSample: audioBlob + referenceText required');
  }
  const formData = new FormData();
  formData.append('audio', audioBlob);
  formData.append('reference_text', referenceText);
  formData.append('language', language);
  try {
    const { data } = await aiClient.post('/tts/clone-voice', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (err) {
    logger.warn('tts_clone_voice_failed', { message: err?.message });
    throw err;
  }
}

/**
 * Submit + poll an F5-TTS personal-narration job.
 *
 * Async-first because the SageMaker endpoint runs with scale-to-zero
 * (idle cost $0). Cold start adds ~30 s on the first call after idle;
 * subsequent calls within ~10 min reuse the warm instance.
 *
 * Pattern:
 *   1. POST /tts/synthesize-cloned → { inference_id, poll_url, poll_after_seconds }
 *   2. Wait ``poll_after_seconds`` then poll ``poll_url``.
 *   3. When ``status === 'ready'`` the response carries an
 *      ``audio_url`` (presigned S3) the browser fetches directly.
 *
 * Returns the same shape as ``synthesizePro`` — ``{ url, cleanup,
 * cached }`` — so the caller can swap providers without code change.
 * ``cleanup`` is a no-op for presigned URLs (no Blob to revoke).
 *
 * ``onProgress`` is called with ``{ elapsed_seconds, attempt }``
 * on every poll so the UI can show a "still working… (15 s)" hint.
 */
/**
 * Submit a personal-narrator (F5-TTS clone) job to the BE and
 * return immediately. Charged once against ``ai_tts`` quota.
 *
 * Returns ``{inferenceId, cached, firstWait}``. Caller polls the
 * job status via ``pollPersonalSynthOnce(inferenceId)``.
 *
 * Split out of the legacy ``synthesizePersonal`` (which still
 * exists as a thin wrapper for callers that want the
 * fire-and-await shape). The split lets the
 * ``PersonalNarrationContext`` own the poll loop so it survives
 * navigation -- the previous in-hook poll died the moment the
 * writer navigated away from the reader, which is exactly what
 * "make it background" requires us to fix.
 */
/**
 * Snapshot of the F5-TTS SageMaker endpoint warmth state.
 * Used to render the warm/cold/warming indicator in the
 * personal-narrator tab. Safe to poll every 30 s while the tab
 * is open (one ``sagemaker:DescribeEndpoint`` per call).
 *
 * Returns ``{ status: 'warm'|'cold'|'warming'|'failed',
 *             current_instances, desired_instances,
 *             endpoint_status }``.
 */
export async function getF5TtsEndpointStatus() {
  const { data } = await aiClient.get('/tts/endpoint-status');
  return data;
}

/**
 * Trigger a 0 → 1 scale-up of the F5-TTS endpoint so the
 * writer's next chapter submission hits warm path (~70 s)
 * instead of cold (5-8 min EC2 provisioning + GPU image pull).
 *
 * Idempotent on the BE: a warmup against an already-warm
 * endpoint is a no-op + status echo. Costs $0 immediately
 * (just a control-plane call); the actual cost is the running
 * instance once provisioned.
 *
 * Returns the same shape as ``getF5TtsEndpointStatus`` plus
 * ``warmup_triggered: boolean`` (false if already warm/warming).
 */
export async function warmupF5TtsEndpoint() {
  const { data } = await aiClient.post('/tts/warmup');
  return data;
}

export async function submitPersonalSynth({
  text,
  voiceSampleS3Key,
  voiceReferenceText,
  language = 'en',
}) {
  if (!text || !voiceSampleS3Key || !voiceReferenceText) {
    throw new Error('submitPersonalSynth: text + voice sample pointer required');
  }
  const verdict = await consumeQuota('ai_tts');
  if (!verdict?.allowed) {
    throw new QuotaExceededError(verdict);
  }
  try {
    const { data } = await aiClient.post('/tts/synthesize-cloned', {
      text,
      voice_sample_s3_key: voiceSampleS3Key,
      voice_reference_text: voiceReferenceText,
      language,
    });
    return {
      inferenceId: data?.inference_id,
      cached: !!data?.cached,
      firstWait: data?.poll_after_seconds != null ? data.poll_after_seconds : 5,
    };
  } catch (err) {
    logger.warn('tts_synthesize_personal_submit_failed', { message: err?.message });
    throw err;
  }
}

/**
 * Single GET against ``/tts/job/{id}``. Returns the raw status
 * object (``{status: 'pending'|'ready'|'failed', audio_url?: ...}``).
 * The context schedules the next call based on backoff.
 */
export async function pollPersonalSynthOnce(inferenceId) {
  if (!inferenceId) throw new Error('pollPersonalSynthOnce: inferenceId required');
  try {
    const { data } = await aiClient.get(`/tts/job/${inferenceId}`);
    return data;
  } catch (err) {
    logger.warn('tts_personal_poll_failed', { inferenceId, message: err?.message });
    throw err;
  }
}

/**
 * Backoff sequence (seconds) for the personal-narrator poll
 * loop. Exported so the context store + the legacy fire-and-await
 * wrapper share the same shape. 5 → 5 → 8 → 12 → 18 → 27 → 30 s
 * repeating; 30 s tail (was 60 s) means worst-case detection
 * latency drops to ~30 s. /tts/job/{id} is a 50 ms S3 HEAD on
 * the BE so doubled poll volume is irrelevant.
 */
export const PERSONAL_POLL_BACKOFFS = [5, 5, 8, 12, 18, 27, 30];

/**
 * 20-minute budget. Was 900 s, bit a writer at 15 min on a long
 * chapter (~10 k char French prose generating ~120 MB MP3). Cold-
 * start path: EC2 provisioning + GPU image pull + handler load is
 * 8-10 min on its own; chapter-length inference adds another
 * 80-180 s depending on length; promote-to-cache S3 copy adds
 * another 3-5 s for the 120 MB blob. Worst-case observed is
 * ~13 min cold + ~3 min long-chapter inference + slack = 17 min.
 * 1200 s leaves safety margin so a timeout reflects a real BE
 * wedge (lost SageMaker job, dropped request) rather than the
 * writer just hitting the cap mid-cold-start on their first
 * uncached chapter. The bg-job pattern in
 * PersonalNarrationContext surfaces a clear "running in
 * background" affordance during this whole window so the writer
 * isn't stuck staring at a spinner.
 */
export const PERSONAL_POLL_MAX_SECONDS = 1200;

/**
 * Legacy fire-and-await shape. Kept for any caller still using
 * it; production reader has moved to the context-driven bg-job
 * path so this is no longer the hot code path.
 */
export async function synthesizePersonal({
  text,
  voiceSampleS3Key,
  voiceReferenceText,
  language = 'en',
  onProgress = null,
  maxPollSeconds = PERSONAL_POLL_MAX_SECONDS,
}) {
  const { inferenceId, cached, firstWait } = await submitPersonalSynth({
    text, voiceSampleS3Key, voiceReferenceText, language,
  });
  const start = Date.now();
  if (cached && onProgress) {
    try { onProgress({ elapsed_seconds: 0, attempt: 0, cached: true }); } catch { /* swallow */ }
  }
  const backoffs = [firstWait, ...PERSONAL_POLL_BACKOFFS];
  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const wait = backoffs[Math.min(attempt, backoffs.length - 1)];
    await new Promise((r) => setTimeout(r, wait * 1000));
    attempt += 1;
    const elapsed = Math.round((Date.now() - start) / 1000);
    if (onProgress) {
      try { onProgress({ elapsed_seconds: elapsed, attempt }); } catch { /* swallow */ }
    }
    if (elapsed > maxPollSeconds) {
      throw new Error(`synthesizePersonal: timed out after ${elapsed}s (likely SageMaker cold-start exceeded budget)`);
    }
    const status = await pollPersonalSynthOnce(inferenceId);
    if (status.status === 'ready' && status.audio_url) {
      return {
        url: status.audio_url, cleanup: () => {},
        cached: !!cached, shared: !!cached,
      };
    }
    if (status.status === 'failed') {
      throw new Error('synthesizePersonal: SageMaker reported failure');
    }
  }
}


/**
 * Download a chapter's narration as an MP3 file. Reuses the
 * Pro-synth pipeline but charges the ``ai_tts_download`` bucket
 * (separate from live-playback ``ai_tts``) so the writer can
 * save offline audiobook chapters without burning their
 * playback budget for the day.
 *
 * Triggers a browser download via the standard
 * ``<a download>`` shape. Returns ``{ filename, cached }`` on
 * success. Throws ``QuotaExceededError`` when the download
 * bucket is exhausted.
 */
export async function downloadChapterAudio({
  text, voiceId, planTier = 'free', engine = null,
  filename = 'chapter.mp3', onProgress = null,
}) {
  const result = await synthesizePro({
    text, voiceId, planTier, engine,
    onProgress,
    quotaFeature: 'ai_tts_download',
  });
  try {
    // Save-to-disk shape. ``result.url`` is either a Blob URL
    // (sync sync path returned audio inline) or a CDN URL (async
    // path returned a presigned link). Both work with the
    // anchor-download trick.
    const a = document.createElement('a');
    a.href = result.url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    if (a.parentNode) a.parentNode.removeChild(a);
    return { filename, cached: result.cached };
  } finally {
    // Revoke the Blob URL once the browser has captured the
    // download. Tiny delay so the click handler completes.
    setTimeout(() => {
      try { result.cleanup?.(); } catch { /* ignore */ }
    }, 1000);
  }
}
